/**
 * Data-driven comparison pages ("AgentMesh vs X") for programmatic SEO.
 * Add a competitor here → a new crawlable /compare/<slug> page + sitemap entry.
 * Keep claims fair and factual (comparison pages rank and convert on trust).
 */

export interface ComparisonRow {
  feature: string;
  agentMesh: string;
  competitor: string;
}

export interface Comparison {
  slug: string;
  competitor: string;
  /**
   * Date the competitor's live site was last checked, and the page checked.
   *
   * Comparison pages are the fastest way to lose a prospect who knows the
   * category: a claim that was true in 2024 reads as either uninformed or
   * evasive, and it discredits every other claim on the site. An August 2026
   * audit found 3 of 3 spot-checked pages materially misrepresenting the
   * competitor's current product.
   *
   * Showing the date is also what makes a self-published comparison credible to
   * a reader and to a retrieval system. Re-verify quarterly and move the date.
   */
  lastVerified: string;
  sourceUrl: string;
  /** H1 on the page. */
  title: string;
  /**
   * <title> override. The default appended " — AgentMesh" to a title that
   * already began "AgentMesh vs …", producing "AgentMesh vs n8n — AgentMesh":
   * the brand twice in 28 characters, and no reason for anyone to click.
   */
  metaTitle: string;
  metaDescription: string;
  intro: string;
  rows: ComparisonRow[];
  faqs: { q: string; a: string }[];
}

const AGENTMESH_TAGLINE =
  'AgentMesh is a no-code platform for building teams of AI agents that reason, use tools, and hand off to each other to run real operations.';

export const comparisons: Comparison[] = [
  {
    slug: 'zapier',
    lastVerified: '2026-08-18',
    sourceUrl: 'https://zapier.com/pricing',
    competitor: 'Zapier',
    title: 'AgentMesh vs Zapier',
    metaTitle:
      'AgentMesh vs Zapier — Flat $29 vs Per-Task Pricing',
    metaDescription:
      'AgentMesh vs Zapier: AI agents that reason and use tools vs trigger→action automation. Compare no-code AI orchestration, tools, scheduling and pricing.',
    intro:
      'Zapier connects apps with trigger→action "Zaps" — great for deterministic, step-by-step automation. AgentMesh adds AI agents that reason over each step, call tools, and collaborate as a team. If your work needs judgment (not just wiring), AgentMesh fits; if it is purely "when X, do Y", Zapier is a fine choice.',
    rows: [
      { feature: 'No-code builder', agentMesh: 'Yes — visual agent canvas', competitor: 'Yes — Zap editor' },
      { feature: 'AI agents that reason', agentMesh: 'Core — LLM agents make decisions', competitor: 'Zapier Agents — a separate product, metered in activities' },
      { feature: 'Multi-agent orchestration', agentMesh: 'Sequential, parallel, conditional, fan-out, DAG', competitor: 'Linear Zaps + paths; Agents run on their own rail' },
      { feature: 'Agent tools', agentMesh: 'HTTP, email, Slack, webhooks, Google Calendar', competitor: '9,000+ app integrations' },
      { feature: 'Scheduling & triggers', agentMesh: 'Cron schedules + inbound webhooks', competitor: 'Schedules + app triggers' },
      { feature: 'Bring your own LLM key', agentMesh: 'Yes — and it removes the daily run limit entirely. No per-task or per-credit fee on top.', competitor: 'Chatbots only — your own OpenAI/Anthropic key. Tasks are still metered.' },
      { feature: 'Pricing', agentMesh: 'Free, Pro $29/mo flat', competitor: 'Free 100 tasks/mo; Pro from $19.99/mo (750 tasks, annual); Team from $69/mo' },
    ],
    faqs: [
      {
        q: 'Is AgentMesh a Zapier alternative?',
        a: 'For AI-driven work, yes. AgentMesh runs teams of AI agents that reason and use tools, where Zapier runs deterministic trigger→action automations. Many teams use both.',
      },
      {
        q: 'Does AgentMesh need code?',
        a: 'No. AgentMesh is a visual, no-code builder — if you can describe how a task should flow, you can build it.',
      },
    ],
  },
  {
    slug: 'n8n',
    lastVerified: '2026-08-18',
    sourceUrl: 'https://n8n.io/pricing/',
    competitor: 'n8n',
    title: 'AgentMesh vs n8n',
    metaTitle:
      'AgentMesh vs n8n — Hosted No-Code vs Self-Hosted',
    metaDescription:
      'AgentMesh vs n8n: hosted no-code AI agents vs developer-oriented workflow automation. Compare AI orchestration, tools, hosting and pricing.',
    intro:
      'n8n is a powerful, developer-friendly workflow automation tool (often self-hosted) built around nodes and expressions. AgentMesh is a hosted, no-code platform focused on AI agents that reason and collaborate. Choose n8n for engineer-owned pipelines; choose AgentMesh for AI agent teams your whole team can build.',
    rows: [
      { feature: 'Audience', agentMesh: 'No-code — operators & SMBs', competitor: 'Developer-oriented' },
      { feature: 'Hosting', agentMesh: 'Fully hosted', competitor: 'Self-host or cloud' },
      { feature: 'AI agents that reason', agentMesh: 'Core', competitor: 'Core — "AI agents and workflows you can see and control"' },
      { feature: 'Multi-agent orchestration', agentMesh: 'Built-in (incl. DAG, structured handoff)', competitor: 'Multi-agent and RAG, built from nodes on a canvas' },
      { feature: 'Agent tools', agentMesh: 'HTTP, email, Slack, webhooks, Google Calendar', competitor: 'Large node library' },
      { feature: 'Marketplace templates', agentMesh: 'Yes — install in a click', competitor: 'Workflow templates' },
      { feature: 'Bring your own LLM key', agentMesh: 'Yes — and it removes the daily run limit entirely. No per-task or per-credit fee on top.', competitor: 'Yes — self-hosted you supply the keys; cloud tiers still meter executions.' },
      { feature: 'Pricing', agentMesh: 'Free, Pro $29/mo flat', competitor: 'Free self-hosted; Starter EUR20/mo (2.5K executions), Pro EUR50/mo (10K), Business EUR667/mo (40K)' },
    ],
    faqs: [
      {
        q: 'Is AgentMesh easier than n8n?',
        a: 'For non-developers, generally yes — AgentMesh is no-code and hosted, and multi-agent orchestration is built in rather than assembled from nodes.',
      },
      {
        q: 'Can AgentMesh call my own APIs like n8n?',
        a: 'Yes — the http_request and webhook tools let agents call any REST API (SSRF-guarded), plus first-class Slack and Google Calendar integrations.',
      },
    ],
  },
  {
    slug: 'make',
    lastVerified: '2026-08-18',
    sourceUrl: 'https://www.make.com/en/pricing',
    competitor: 'Make',
    title: 'AgentMesh vs Make (Integromat)',
    metaTitle:
      'AgentMesh vs Make — Flat Pricing vs Credit Metering',
    metaDescription:
      'AgentMesh vs Make (Integromat): AI agent teams vs visual scenario automation. Compare no-code AI orchestration, tools, scheduling and pricing.',
    intro:
      'Make (formerly Integromat) builds visual "scenarios" that move data between apps. AgentMesh builds teams of AI agents that reason over the work and hand off to each other. If you need branching data plumbing, Make is strong; if you need agents that make decisions and act, AgentMesh is built for it.',
    rows: [
      { feature: 'No-code builder', agentMesh: 'Yes — agent canvas', competitor: 'Yes — scenario editor' },
      { feature: 'AI agents that reason', agentMesh: 'Core', competitor: 'Make AI Agents (beta) — agents that choose their next step' },
      { feature: 'Multi-agent orchestration', agentMesh: 'Sequential, parallel, conditional, fan-out, DAG', competitor: 'Routers & filters' },
      { feature: 'Agent tools', agentMesh: 'HTTP, email, Slack, webhooks, Google Calendar', competitor: '3,000+ apps plus 350+ AI apps' },
      { feature: 'Scheduling & triggers', agentMesh: 'Cron + inbound webhooks', competitor: 'Schedules + instant triggers' },
      { feature: 'Bring your own LLM key', agentMesh: 'Yes — and it removes the daily run limit entirely. No per-task or per-credit fee on top.', competitor: 'Yes — but operations still cost Make credits when you use your own key.' },
      { feature: 'Pricing', agentMesh: 'Free, Pro $29/mo flat', competitor: 'Free 1,000 credits/mo; Core $9/mo, Pro $16/mo, Teams $29/mo' },
    ],
    faqs: [
      {
        q: 'Is AgentMesh a Make alternative?',
        a: 'For AI-agent work, yes. AgentMesh centers on AI agents that reason and collaborate, where Make centers on moving data between apps.',
      },
      {
        q: 'What can AgentMesh agents actually do?',
        a: AGENTMESH_TAGLINE,
      },
    ],
  },
];

comparisons.push(
  {
    slug: 'lindy',
    lastVerified: '2026-08-18',
    sourceUrl: 'https://www.lindy.ai/pricing',
    competitor: 'Lindy',
    title: 'AgentMesh vs Lindy',
    metaTitle:
      'AgentMesh vs Lindy — Flat $29 vs Per-Seat Pricing',
    metaDescription:
      'AgentMesh vs Lindy: multi-agent orchestration on a no-code canvas vs AI assistants. Compare agent teams, tools, templates and pricing.',
    intro:
      'Lindy builds AI assistants that automate individual tasks. AgentMesh focuses on teams of agents that hand off to each other in orchestrated workflows — sequential, parallel, conditional, fan-out, DAG, or loop — with a marketplace of templates and transparent $29/mo pricing.',
    rows: [
      { feature: 'No-code builder', agentMesh: 'Yes — visual agent canvas', competitor: 'Yes' },
      { feature: 'AI agents that reason', agentMesh: 'Core', competitor: 'Core' },
      { feature: 'Multi-agent orchestration', agentMesh: 'Sequential, parallel, conditional, fan-out, DAG, loop', competitor: 'Task-focused assistants' },
      { feature: 'Agent tools', agentMesh: 'HTTP, email, Slack, webhooks, Google Calendar', competitor: 'Integrations' },
      { feature: 'Marketplace templates', agentMesh: 'Yes — 34+ templates, install in a click', competitor: 'Prebuilt "Lindies"' },
      { feature: 'Bring your own LLM key', agentMesh: 'Yes — and it removes the daily run limit entirely. No per-task or per-credit fee on top.', competitor: 'Models are bundled and chosen per plan; credits still apply.' },
      { feature: 'Pricing', agentMesh: 'Free, Pro $29/mo flat — one price, any number of people', competitor: 'Per user: Plus $29.99, Pro $99.99, Max $199.99 per month, each with a credit allowance' },
    ],
    faqs: [
      { q: 'Is AgentMesh a Lindy alternative?', a: 'Yes. Both build AI agents; AgentMesh emphasizes orchestrating teams of agents in explicit workflows, with a template marketplace and a flat $29/mo Pro plan.' },
      { q: 'Can AgentMesh agents work together?', a: 'Yes — you compose multiple agents on a canvas with sequential, parallel, conditional, fan-out, DAG, or loop execution and structured hand-offs.' },
    ],
  },
  {
    slug: 'relevance-ai',
    lastVerified: '2026-08-18',
    sourceUrl: 'https://relevanceai.com/pricing',
    competitor: 'Relevance AI',
    title: 'AgentMesh vs Relevance AI',
    metaTitle:
      'AgentMesh vs Relevance AI — SMB vs Enterprise Agents',
    metaDescription:
      'AgentMesh vs Relevance AI: no-code AI agent teams with tools and automation vs an AI workforce platform. Compare orchestration, tools and pricing.',
    intro:
      'Relevance AI markets an "AI workforce" of agents. AgentMesh is a no-code platform for teams of agents that reason, use tools, and run on schedules or webhook triggers — with explicit orchestration modes and simple $29/mo pricing.',
    rows: [
      { feature: 'No-code builder', agentMesh: 'Yes — visual canvas', competitor: 'Yes' },
      { feature: 'Multi-agent orchestration', agentMesh: 'Sequential, parallel, conditional, fan-out, DAG, loop', competitor: 'Agent teams' },
      { feature: 'Agent tools', agentMesh: 'HTTP, email, Slack, webhooks, Google Calendar', competitor: 'Tools/integrations' },
      { feature: 'Automation', agentMesh: 'Cron schedules + inbound webhook triggers', competitor: 'Triggers' },
      { feature: 'Marketplace templates', agentMesh: 'Yes — install in a click', competitor: 'Templates' },
      { feature: 'Bring your own LLM key', agentMesh: 'Yes — and it removes the daily run limit entirely. No per-task or per-credit fee on top.', competitor: 'Not published.' },
      { feature: 'Pricing', agentMesh: 'Free, Pro $29/mo — published', competitor: 'Not published — contact sales' },
    ],
    faqs: [
      { q: 'Is AgentMesh a Relevance AI alternative?', a: 'Yes, for teams that want no-code AI agents with explicit orchestration and flat pricing. Both let agents collaborate; AgentMesh centers the visual workflow + marketplace.' },
      { q: 'Does AgentMesh run agents on a schedule?', a: 'Yes — cron schedules run workflows automatically, and inbound webhooks let external events trigger a run.' },
    ],
  },
  {
    slug: 'gumloop',
    lastVerified: '2026-08-18',
    sourceUrl: 'https://www.gumloop.com/pricing',
    competitor: 'Gumloop',
    title: 'AgentMesh vs Gumloop',
    metaTitle:
      'AgentMesh vs Gumloop — Flat $29 vs Credits + 8% Fee',
    metaDescription:
      'AgentMesh vs Gumloop: agent-native orchestration vs node-based no-code AI automation. Compare AI reasoning, tools, templates and pricing.',
    intro:
      'Gumloop builds no-code AI automations on a node canvas. AgentMesh is agent-native: you compose AI agents that reason and hand off to each other, with tools, scheduling, a marketplace, and flat $29/mo pricing.',
    rows: [
      { feature: 'No-code builder', agentMesh: 'Yes — agent canvas', competitor: 'Yes — node canvas' },
      { feature: 'AI agents that reason', agentMesh: 'Core — agent-native', competitor: 'AI nodes in flows' },
      { feature: 'Multi-agent orchestration', agentMesh: 'Sequential, parallel, conditional, fan-out, DAG, loop', competitor: 'Node graphs' },
      { feature: 'Agent tools', agentMesh: 'HTTP, email, Slack, webhooks, Google Calendar', competitor: 'Node library' },
      { feature: 'Automation', agentMesh: 'Cron schedules + webhook triggers', competitor: 'Schedules + triggers' },
      { feature: 'Bring your own LLM key', agentMesh: 'Yes — and it removes the daily run limit entirely. No per-task or per-credit fee on top.', competitor: 'Yes — plus an 8% orchestration fee on top.' },
      { feature: 'Pricing', agentMesh: 'Free, Pro $29/mo flat', competitor: 'No free tier — 14-day trial; Pro from $37/mo (20K credits) plus an 8% orchestration fee' },
    ],
    faqs: [
      { q: 'Is AgentMesh a Gumloop alternative?', a: 'Yes. Gumloop centers node-based flows; AgentMesh centers AI agents that reason and collaborate as a team, with orchestration modes and a template marketplace.' },
      { q: 'What can AgentMesh agents do?', a: AGENTMESH_TAGLINE },
    ],
  }
);

comparisons.push(
  {
    slug: 'crewai',
    lastVerified: '2026-08-18',
    sourceUrl: 'https://www.crewai.com/pricing',
    competitor: 'CrewAI',
    title: 'AgentMesh vs CrewAI',
    metaTitle:
      'AgentMesh vs CrewAI — No-Code vs Developer Framework',
    metaDescription:
      'AgentMesh vs CrewAI: no-code hosted multi-agent platform vs an open-source Python framework. Compare setup, orchestration, hosting and pricing.',
    intro:
      'CrewAI is an open-source Python framework for building multi-agent systems in code. AgentMesh is a no-code, hosted platform for the same goal — teams of agents that collaborate — but you build on a visual canvas and it runs for you. Choose CrewAI to own the code; choose AgentMesh to ship without engineering.',
    rows: [
      { feature: 'Audience', agentMesh: 'No-code — operators & SMBs', competitor: 'Developers (Python)' },
      { feature: 'Setup', agentMesh: 'Sign up and build in the browser', competitor: 'Install, code, deploy yourself' },
      { feature: 'Hosting', agentMesh: 'Fully hosted', competitor: 'You host it' },
      { feature: 'Multi-agent orchestration', agentMesh: 'Sequential, parallel, conditional, fan-out, DAG, loop', competitor: 'Crews and flows — now with a visual editor and AI copilot' },
      { feature: 'Agent tools', agentMesh: 'Built-in: HTTP, email, Slack, webhooks, Google Calendar', competitor: 'Code your own tools' },
      { feature: 'Marketplace templates', agentMesh: 'Yes — install in a click', competitor: 'Community examples' },
      { feature: 'Bring your own LLM key', agentMesh: 'Yes — and it removes the daily run limit entirely. No per-task or per-credit fee on top.', competitor: 'Yes — you configure the model, and pay the provider directly.' },
      { feature: 'Pricing', agentMesh: 'Free, Pro $29/mo flat', competitor: 'Basic free (50 workflow runs/mo); Enterprise custom' },
    ],
    faqs: [
      { q: 'Is AgentMesh a no-code CrewAI alternative?', a: 'Yes. CrewAI is a code-first framework; AgentMesh gives you multi-agent orchestration without writing or hosting code.' },
      { q: 'Can I still use my own LLM?', a: 'Yes — bring your own provider key (encrypted per user), or use the platform’s.' },
    ],
  },
  {
    slug: 'dust',
    lastVerified: '2026-08-18',
    sourceUrl: 'https://dust.tt/pricing',
    competitor: 'Dust',
    title: 'AgentMesh vs Dust',
    metaTitle:
      'AgentMesh vs Dust — Flat $29 vs Per-Seat AI Workspace',
    metaDescription:
      'AgentMesh vs Dust: no-code agent teams with orchestration and automation vs an AI assistant platform for teams. Compare features and pricing.',
    intro:
      'Dust helps teams build AI assistants over their company knowledge. AgentMesh focuses on teams of agents that take actions and run on their own — with explicit orchestration modes, a template marketplace, and flat $29/mo pricing.',
    rows: [
      { feature: 'No-code builder', agentMesh: 'Yes — agent canvas', competitor: 'Yes' },
      { feature: 'Multi-agent orchestration', agentMesh: 'Sequential, parallel, conditional, fan-out, DAG, loop', competitor: 'Assistants + apps' },
      { feature: 'Agent tools', agentMesh: 'HTTP, email, Slack, webhooks, Google Calendar', competitor: 'Connections/tools' },
      { feature: 'Automation', agentMesh: 'Cron schedules + inbound webhook triggers', competitor: 'Triggers' },
      { feature: 'Marketplace templates', agentMesh: 'Yes — install in a click', competitor: 'Templates' },
      { feature: 'Bring your own LLM key', agentMesh: 'Yes — and it removes the daily run limit entirely. No per-task or per-credit fee on top.', competitor: 'Not published — 20+ models bundled, metered in credits.' },
      { feature: 'Pricing', agentMesh: 'Free, Pro $29/mo flat — one price, any number of people', competitor: 'Per seat: Pro ~$30/seat/mo, Max ~$150/seat/mo, plus credit allowances' },
    ],
    faqs: [
      { q: 'Is AgentMesh a Dust alternative?', a: 'Yes, for teams that want agents that act and run automatically, composed in explicit workflows, at flat pricing.' },
      { q: 'Can agents act, not just answer?', a: 'Yes — agents call APIs, send email/Slack, fire webhooks and update your calendar during a run.' },
    ],
  },
  {
    slug: 'stack-ai',
    lastVerified: '2026-08-18',
    sourceUrl: 'https://www.stackai.com/pricing',
    competitor: 'Stack AI',
    title: 'AgentMesh vs Stack AI',
    metaTitle:
      'AgentMesh vs Stack AI — SMB Agents vs Enterprise LLM',
    metaDescription:
      'AgentMesh vs Stack AI: no-code AI agent teams with orchestration, tools and a marketplace. Compare multi-agent workflows, automation and pricing.',
    intro:
      'Stack AI is a no-code builder for AI agents and workflows. AgentMesh covers the same no-code ground and leans into teams of agents with explicit orchestration (including loops and DAGs), built-in tools, automation, and a template marketplace at flat $29/mo.',
    rows: [
      { feature: 'No-code builder', agentMesh: 'Yes — agent canvas', competitor: 'Yes' },
      { feature: 'Multi-agent orchestration', agentMesh: 'Sequential, parallel, conditional, fan-out, DAG, loop', competitor: 'Workflows' },
      { feature: 'Agent tools', agentMesh: 'HTTP, email, Slack, webhooks, Google Calendar', competitor: 'Integrations' },
      { feature: 'Automation', agentMesh: 'Cron schedules + webhook triggers', competitor: 'Triggers' },
      { feature: 'Marketplace templates', agentMesh: 'Yes — install in a click', competitor: 'Templates' },
      { feature: 'Bring your own LLM key', agentMesh: 'Yes — and it removes the daily run limit entirely. No per-task or per-credit fee on top.', competitor: 'Not published.' },
      { feature: 'Pricing', agentMesh: 'Free, Pro $29/mo flat', competitor: 'Free 500 runs/mo (1 seat, 2 projects); Enterprise custom' },
    ],
    faqs: [
      { q: 'Is AgentMesh a Stack AI alternative?', a: 'Yes. Both are no-code AI agent builders; AgentMesh emphasizes orchestrating teams of agents and running them on schedules or triggers.' },
      { q: 'What orchestration modes are supported?', a: 'Sequential, parallel, conditional, fan-out, dependency graphs (DAG), and loops — with structured hand-offs between agents.' },
    ],
  }
);

comparisons.push({
  slug: 'bardeen',
  lastVerified: '2026-08-18',
  sourceUrl: 'https://www.bardeen.ai/pricing',
  competitor: 'Bardeen',
  title: 'AgentMesh vs Bardeen',
    metaTitle:
      'AgentMesh vs Bardeen — AI Agent Teams vs GTM Scraping',
  metaDescription:
    'AgentMesh vs Bardeen: hosted multi-agent orchestration vs browser-based AI automation. Compare agent teams, tools, scheduling and pricing.',
  intro:
    'Bardeen automates tasks with AI, often in the browser, across your web apps. AgentMesh is a hosted platform for teams of AI agents that reason and hand off to each other in orchestrated workflows, run on schedules or webhook triggers, with a template marketplace and flat $29/mo pricing.',
  rows: [
    { feature: 'No-code builder', agentMesh: 'Yes — agent canvas', competitor: 'Yes' },
    { feature: 'Where it runs', agentMesh: 'Hosted (server-side)', competitor: 'Browser + cloud' },
    { feature: 'Multi-agent orchestration', agentMesh: 'Sequential, parallel, conditional, fan-out, DAG, loop', competitor: 'Automation flows' },
    { feature: 'Agent tools', agentMesh: 'HTTP, email, Slack, webhooks, Google Calendar', competitor: 'App actions' },
    { feature: 'Automation', agentMesh: 'Cron schedules + inbound webhook triggers', competitor: 'Triggers' },
    { feature: 'Bring your own LLM key', agentMesh: 'Yes — and it removes the daily run limit entirely. No per-task or per-credit fee on top.', competitor: 'Not published.' },
    { feature: 'Pricing', agentMesh: 'Free, Pro $29/mo flat', competitor: '100 free credits/mo; Basic $10/mo (100 credits), Premium $50/mo (1,000)' },
  ],
  faqs: [
    { q: 'Is AgentMesh a Bardeen alternative?', a: 'Yes, for server-side teams of agents that reason, use tools, and run automatically — composed in explicit workflows at flat pricing.' },
    { q: 'Does AgentMesh run in the browser?', a: 'No — agents run hosted on the server, so workflows keep running on schedules and triggers without a browser open.' },
  ],
});

export function getComparison(slug: string): Comparison | undefined {
  return comparisons.find((c) => c.slug === slug);
}
